import {
  makeStyles,
  mergeClasses,
  tokens,
  Caption1,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  type ButtonProps,
} from "@fluentui/react-components";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { MEMBER_PLAN_ROLES } from "@/features/access/can";
import { AccountSettingsDialog } from "@/features/access/AccountSettingsDialog";
import { PlanSettingsDialog, type PlanSettingsTab } from "@/features/access/PlanSettingsDialog";
import { PlanShareDialog } from "@/features/access/PlanShareDialog";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { peopleOnPlan } from "@/features/access/own-assignment";
import { noteRoleDemotion } from "@/features/access/role-demotion";
import { planRoleLabel, useCan, useWorkspaceAdminView } from "@/features/access/use-plan-access";
import { PROJECT_NAV, pathnameOpensPlan, planHref, projectIdFromPath, tabFromPath, type ProjectNavGroup } from "./project-nav";
import {
  Apps20Filled,
  Apps20Regular,
  ArrowCounterclockwise20Regular,
  ArrowEnter20Regular,
  ArrowExit20Regular,
  ArrowLeft20Regular,
  Beaker20Regular,
  Board20Filled,
  Board20Regular,
  Bug20Regular,
  ChartMultiple20Filled,
  ChartMultiple20Regular,
  ChevronLeft16Regular,
  ChevronRight12Regular,
  Color20Regular,
  Document20Filled,
  Document20Regular,
  Edit20Regular,
  Eye20Regular,
  Folder20Filled,
  Folder20Regular,
  Globe20Regular,
  History20Filled,
  History20Regular,
  Home20Filled,
  Home20Regular,
  Key20Regular,
  Keyboard20Regular,
  LocalLanguage20Regular,
  LockClosed16Regular,
  People20Filled,
  People20Regular,
  PeopleTeam20Filled,
  PeopleTeam20Regular,
  Person20Regular,
  PersonGuest20Regular,
  PersonSettings20Regular,
  Settings16Regular,
  Settings20Regular,
  Shield20Regular,
  Tab20Regular,
  TaskListSquareLtr20Filled,
  TaskListSquareLtr20Regular,
  WeatherMoon20Regular,
  WeatherSunny20Regular,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { useSurfaceLocation } from "./settings-url";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AppDialog, ConfirmDialog, DialogActionsBar } from "@/components/app-dialog";
import { signOut, useSession } from "@/features/access/session";
import { planVisibilityOf, showInPlanList, usePlanVisibilityTick } from "@/features/access/plan-visibility";
import { canWorkspace, useWorkspaceRole } from "@/features/access/workspace-access";
import { FuiButton } from "@/components/fui-control";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { modKey } from "@/lib/format";
import { useCatalogStore } from "@/store/catalog-store";
import type { ProjectTab } from "@/store/persist";
import { useProjectStore } from "@/store/project-store";
import { recordPhase } from "@/store/record-ops";
import { useSettingsHub } from "./settings-hub";
import { CHROME_HIDE_MS, CHROME_NAV_GAP_PX, CHROME_ROW_PX, NAV_WIDTH_MS } from "@/desktop/window-chrome";
import { ChromeSearch, ChromeToggle } from "./ChromeToggle";
import { setShareOpen, useShareOpen } from "./share-open";
import { setTabLabCount, useTabLabCount, type TabLabCount } from "./tab-lab";

/** 접기·펼치기. 클릭 직후 움직이고 끝에서 짧게 안착한다. */
/** 사이드바 너비와 같은 길이. 줄 높이와 글자 페이드가 너비보다 늦게 끝나지 않게 한다. */
const NAV_MOTION = `${NAV_WIDTH_MS}ms`;
const NAV_EASE = "cubic-bezier(0.2, 0, 0, 1)";
const NAV_WIDTH = "220px";
const RAIL_WIDTH = "48px";
const NAV_ITEM_HEIGHT = "28px";
const NAV_ITEM_HEIGHT_COMPACT = "28px";

const useStyles = makeStyles({
  slot: {
    position: "relative",
    overflow: "hidden",
    flexShrink: 0,
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground3,
    ":hover .nav-hide-reveal": {
      maxWidth: "28px",
      opacity: 1,
    },
    ":focus-within .nav-hide-reveal": {
      maxWidth: "28px",
      opacity: 1,
    },
  },
  inner: {
    width: NAV_WIDTH,
    minWidth: NAV_WIDTH,
    height: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    opacity: 1,
    transitionProperty: "opacity",
    transitionDuration: "200ms",
    transitionDelay: "100ms",
    transitionTimingFunction: "ease-in-out",
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
      transitionDelay: "0ms",
    },
  },
  /* 크롬 줄. 웹은 왼쪽 로고, 오른쪽 끝은 검색 다음 접기. macOS 창에서는 로고를 숨긴다. */
  chrome: {
    position: "absolute",
    zIndex: 3,
    top: 0,
    left: 0,
    width: NAV_WIDTH,
    height: `${CHROME_ROW_PX}px`,
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: `${CHROME_NAV_GAP_PX}px`,
    paddingRight: tokens.spacingHorizontalS,
  },
  brand: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    flexShrink: 0,
    margin: 0,
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    background: "transparent",
    color: tokens.colorNeutralForeground1,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  chromeEnd: {
    display: "flex",
    alignItems: "center",
    gap: `${CHROME_NAV_GAP_PX}px`,
    flexShrink: 0,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
    /* 페이지 헤더와 같은 44px 크롬 줄. 신호등은 이 줄 가운데에 둔다. */
    height: "44px",
    minHeight: "44px",
    boxSizing: "border-box",
    padding: `0 ${tokens.spacingHorizontalS}`,
    overflow: "hidden",
    borderBottomWidth: "0px",
    borderBottomStyle: "solid",
    borderBottomColor: "transparent",
    transitionProperty: "height, min-height, border-bottom-width, border-bottom-color",
    transitionDuration: NAV_MOTION,
    transitionTimingFunction: NAV_EASE,
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  /* 레일로 접힐 때 헤더를 떼지 않고 높이를 걷어, 아래 항목이 점프하지 않게 한다. */
  headerShut: {
    height: "0px",
    minHeight: "0px",
    borderBottomWidth: "0px",
    borderBottomColor: "transparent",
    pointerEvents: "none",
  },
  headerRail: {
    width: RAIL_WIDTH,
    minWidth: RAIL_WIDTH,
    maxWidth: RAIL_WIDTH,
    boxSizing: "border-box",
    justifyContent: "center",
    alignItems: "center",
    gap: "2px",
    paddingLeft: 0,
    paddingRight: 0,
  },
  hideReveal: {
    minWidth: 0,
    maxWidth: 0,
    opacity: 0,
    overflow: "hidden",
    flexShrink: 0,
    transitionProperty: "max-width, opacity",
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
  },
  searchRow: {
    flexShrink: 0,
    padding: `2px ${tokens.spacingHorizontalS} 0`,
  },
  search: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    height: NAV_ITEM_HEIGHT,
    marginBottom: 0,
    padding: "0 6px",
    border: "none",
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground2,
    fontSize: "13px",
    lineHeight: "18px",
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    textAlign: "left",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Selected,
      color: tokens.colorNeutralForeground1,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground3Selected,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  searchLabel: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  slash: {
    flexShrink: 0,
    fontSize: "13px",
    lineHeight: "18px",
    color: tokens.colorNeutralForeground3,
  },
  viewport: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  track: {
    display: "flex",
    width: `calc(${NAV_WIDTH} * 2)`,
    height: "100%",
    transform: "translateX(0)",
    transitionProperty: "transform",
    transitionDuration: NAV_MOTION,
    transitionTimingFunction: NAV_EASE,
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  trackProject: {
    transform: `translateX(calc(${NAV_WIDTH} * -1))`,
  },
  pane: {
    width: NAV_WIDTH,
    minWidth: NAV_WIDTH,
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    opacity: 1,
    transitionProperty: "opacity",
    transitionDuration: NAV_MOTION,
    transitionTimingFunction: NAV_EASE,
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  paneAway: {
    opacity: 0,
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "2px",
    padding: `4px ${tokens.spacingHorizontalS} 4px`,
  },
  bodyScroll: {
    gap: "2px",
    overflowX: "hidden",
    overflowY: "auto",
    scrollbarWidth: "thin",
  },
  footerWrap: {
    flexShrink: 0,
    padding: `0 ${tokens.spacingHorizontalS} ${tokens.spacingVerticalS}`,
  },
  planSwitcherRow: {
    gap: "2px",
  },
  planMenu: {
    display: "flex",
    flex: 1,
    minWidth: 0,
    width: "100%",
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    background: "transparent",
    color: tokens.colorNeutralForeground2,
    cursor: "pointer",
    flexShrink: 0,
    transitionProperty: "background-color, color",
    transitionDuration: tokens.durationNormal,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  planTrigger: {
    display: "flex",
    alignItems: "center",
    gap: "3px",
    flex: 1,
    width: "100%",
    minWidth: 0,
    height: "28px",
    padding: "0 6px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    background: "transparent",
    color: tokens.colorNeutralForeground1,
    cursor: "pointer",
    textAlign: "left",
    transitionProperty: "background-color",
    transitionDuration: tokens.durationNormal,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  planTriggerOpen: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  planName: {
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: tokens.fontWeightRegular,
    letterSpacing: "0",
    color: "var(--nav-ink)",
  },
  /** 펼친 플랜 사이드 맨 위. 접히면 높이를 걷고, 레일의 설정 아이콘이 대신한다. */
  planIdentitySlot: {
    display: "grid",
    gridTemplateRows: "1fr",
    flexShrink: 0,
    transitionProperty: "grid-template-rows, margin-bottom",
    transitionDuration: NAV_MOTION,
    transitionTimingFunction: NAV_EASE,
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  planIdentitySlotShut: {
    gridTemplateRows: "0fr",
    marginBottom: "-2px",
    pointerEvents: "none",
  },
  planIdentity: {
    minHeight: 0,
    overflow: "hidden",
  },
  planIdentityRow: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    padding: "2px 0 4px",
  },
  planBack: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flex: "1 1 auto",
    minWidth: 0,
    height: "28px",
    margin: 0,
    padding: "0 4px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    background: "transparent",
    color: tokens.colorNeutralForeground2,
    cursor: "pointer",
    textAlign: "left",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  planBackMark: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    flexShrink: 0,
  },
  planIdentityName: {
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: tokens.fontWeightRegular,
    letterSpacing: "0",
    color: "var(--nav-ink)",
  },
  planIdentityIcon: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
    "& .fui-Button__icon": {
      color: tokens.colorNeutralForeground2,
    },
    ":hover": {
      color: tokens.colorNeutralForeground1,
    },
    "&&:hover .fui-Button__icon": {
      color: tokens.colorNeutralForeground1,
    },
    "&&:hover:active .fui-Button__icon": {
      color: tokens.colorNeutralForeground1,
    },
  },
  /** 이름 줄 오른쪽. 왼쪽 뒤로 버튼(28px)보다 한 단계 작다. */
  planSettingsIcon: {
    "&&": {
      width: "24px",
      minWidth: "24px",
      maxWidth: "24px",
      height: "24px",
      minHeight: "24px",
      maxHeight: "24px",
    },
    "& .fui-Button__icon": {
      width: "16px",
      height: "16px",
      fontSize: "16px",
    },
  },
  planChevron: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    width: "14px",
    height: "14px",
  },
  planMenuList: {
    minWidth: "200px",
    maxWidth: "280px",
    maxHeight: "360px",
  },
  group: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    width: "100%",
    height: "24px",
    marginTop: "4px",
    marginBottom: "2px",
    padding: "0 6px",
    border: "none",
    background: "transparent",
    color: tokens.colorNeutralForeground3,
    fontSize: "11px",
    lineHeight: "14px",
    fontWeight: tokens.fontWeightRegular,
    textAlign: "left",
    overflow: "hidden",
    transitionProperty: "height, margin-top, margin-bottom, opacity",
    transitionDuration: NAV_MOTION,
    transitionTimingFunction: NAV_EASE,
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  groupHeaderRail: {
    height: "0px",
    marginTop: "0px",
    marginBottom: "0px",
    opacity: 0,
    pointerEvents: "none",
  },
  groupHeaderFirst: {
    marginTop: 0,
  },
  groupLabel: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  groupInner: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  railDivider: {
    height: "0px",
    flexShrink: 0,
    marginTop: "0px",
    marginBottom: "0px",
    marginLeft: "8px",
    marginRight: "8px",
    width: "32px",
    opacity: 0,
    overflow: "hidden",
    backgroundColor: tokens.colorNeutralStroke2,
    transitionProperty: "height, margin-top, margin-bottom, opacity",
    transitionDuration: NAV_MOTION,
    transitionTimingFunction: NAV_EASE,
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  railDividerOn: {
    height: "1px",
    marginTop: "6px",
    marginBottom: "6px",
    opacity: 1,
  },
  /* 펼친 그룹 사이. 1px 테두리는 점이 붙어서 실선처럼 보이므로 반복 그라데이션으로 점선을 만든다. */
  groupRule: {
    flexShrink: 0,
    height: "1px",
    marginTop: "8px",
    marginBottom: "6px",
    marginLeft: "6px",
    marginRight: "6px",
    backgroundImage: `repeating-linear-gradient(to right, color-mix(in srgb, ${tokens.colorNeutralForeground3} 45%, transparent) 0 1px, transparent 1px 4px)`,
  },
  railSlot: {
    display: "grid",
    gridTemplateRows: "0fr",
    /* 높이가 0이어도 flex gap이 남아 아래 항목이 2px 밀리지 않게 상쇄한다. */
    marginBottom: "-2px",
    pointerEvents: "none",
    transitionProperty: "grid-template-rows, margin-bottom",
    transitionDuration: NAV_MOTION,
    transitionTimingFunction: NAV_EASE,
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  railSlotOpen: {
    gridTemplateRows: "1fr",
    marginBottom: "0px",
    pointerEvents: "auto",
  },
  railSlotInner: {
    minHeight: 0,
    overflow: "hidden",
  },
  /* 접히면 검색·다시 열기가 로고 아래 레일로 들어온다. 펼치면 높이를 걷는다. */
  railUtility: {
    display: "grid",
    gridTemplateRows: "0fr",
    flexShrink: 0,
    transitionProperty: "grid-template-rows",
    transitionDuration: NAV_MOTION,
    transitionTimingFunction: NAV_EASE,
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  railUtilityOpen: {
    gridTemplateRows: "1fr",
  },
  railUtilityInner: {
    minHeight: 0,
    overflow: "hidden",
  },
  railUtilityPad: {
    display: "flex",
    alignItems: "center",
    padding: `2px ${tokens.spacingHorizontalS}`,
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalXS,
    width: "100%",
    minWidth: 0,
  },
  labelText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  count: {
    flexShrink: 0,
    fontSize: "12px",
    lineHeight: "16px",
    color: tokens.colorNeutralForeground1,
    fontVariantNumeric: "tabular-nums",
  },
  settingsSlot: {
    marginTop: "auto",
    paddingTop: tokens.spacingVerticalS,
    flexShrink: 0,
  },
  planFooter: {
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS} ${tokens.spacingVerticalS}`,
  },
  switcherRow: {
    flexShrink: 0,
    display: "flex",
    boxSizing: "border-box",
    height: "40px",
    padding: `4px ${tokens.spacingHorizontalS}`,
    alignItems: "center",
    overflow: "hidden",
    transitionProperty: "height, opacity",
    transitionDuration: NAV_MOTION,
    transitionTimingFunction: NAV_EASE,
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  switcherRowWorkspace: {
    height: "40px",
    padding: "4px 8px",
  },
  switcherRowShut: {
    height: "0px",
    opacity: 0,
    pointerEvents: "none",
  },
  switcherSlot: {
    minWidth: 0,
    flex: 1,
    display: "flex",
  },
  /* 한 줄 28px. 글자는 레귤러·진한 잉크. 선택은 굵기가 아니라 둥근 무채 배경으로 구분한다. */
  item: {
    position: "relative",
    overflow: "visible",
    justifyContent: "flex-start",
    width: "100%",
    height: NAV_ITEM_HEIGHT,
    minHeight: NAV_ITEM_HEIGHT,
    maxHeight: NAV_ITEM_HEIGHT,
    paddingLeft: "8px",
    paddingRight: "8px",
    flexGrow: 0,
    flexShrink: 0,
    minWidth: 0,
    borderRadius: tokens.borderRadiusLarge,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0",
    "&&": {
      fontWeight: tokens.fontWeightRegular,
      color: "var(--nav-ink)",
    },
    "& .fui-Button__icon": {
      marginRight: "10px",
      width: "18px",
      height: "18px",
      fontSize: "18px",
      color: "var(--nav-ink)",
    },
    "& .fui-Button__icon svg": {
      width: "18px",
      height: "18px",
    },
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Selected,
      color: "var(--nav-ink)",
    },
    /* Fluent subtle 버튼의 브랜드색 아이콘 호버를 끄고 글자와 같은 잉크로 둔다. */
    "&&:hover .fui-Button__icon": {
      color: "var(--nav-ink)",
    },
    "&&:hover:active .fui-Button__icon": {
      color: "var(--nav-ink)",
    },
    "&&:active:focus-visible .fui-Button__icon": {
      color: "var(--nav-ink)",
    },
  },
  itemCompact: {
    height: NAV_ITEM_HEIGHT_COMPACT,
    minHeight: NAV_ITEM_HEIGHT_COMPACT,
    maxHeight: NAV_ITEM_HEIGHT_COMPACT,
  },
  /* 레일에서는 글자를 흐름에서 빼고, 아이콘만 32px 안에 둔다. 배경이 레일 밖에서 잘리지 않는다. */
  itemRail: {
    width: "32px",
    minWidth: "32px",
    maxWidth: "32px",
    paddingLeft: "0px",
    paddingRight: "0px",
    justifyContent: "center",
    "&& .fui-Button__icon": {
      marginRight: "0px",
      marginLeft: "0px",
    },
  },
  railLabel: {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: "0px",
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: "0",
  },
  /* 선택: 굵기를 올리지 않고, 바닥보다 한 단계 어두운 둥근 배경과 채운 아이콘으로 구분한다. */
  itemOn: {
    backgroundColor: "var(--nav-on)",
    color: "var(--nav-ink-on)",
    ":hover": {
      backgroundColor: "var(--nav-on)",
      color: "var(--nav-ink-on)",
    },
    ":active": {
      backgroundColor: "var(--nav-on)",
    },
    "& .fui-Button__icon": {
      color: "var(--nav-ink-on)",
    },
    "&&:hover .fui-Button__icon": {
      color: "var(--nav-ink-on)",
    },
    "&&:hover:active .fui-Button__icon": {
      color: "var(--nav-ink-on)",
    },
    "&&:active:focus-visible .fui-Button__icon": {
      color: "var(--nav-ink-on)",
    },
    "@media (forced-colors: active)": {
      outline: "2px solid Highlight",
    },
  },
  fade: {
    opacity: 1,
    transitionProperty: "opacity",
    /* 펼칠 때 글자가 너비와 같이 들어온다. */
    transitionDuration: NAV_MOTION,
    transitionDelay: "0ms",
    transitionTimingFunction: NAV_EASE,
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
      transitionDelay: "0ms",
    },
  },
  fadeHidden: {
    opacity: 0,
    /* 접을 때 글자는 너비보다 먼저 걷힌다. */
    transitionDuration: `${CHROME_HIDE_MS}ms`,
    transitionDelay: "0ms",
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  planListSlot: {
    display: "grid",
    gridTemplateRows: "1fr",
    minHeight: 0,
    transitionProperty: "grid-template-rows",
    transitionDuration: NAV_MOTION,
    transitionTimingFunction: NAV_EASE,
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  planListSlotShut: {
    gridTemplateRows: "0fr",
  },
  planListSlotInner: {
    minHeight: 0,
    overflow: "hidden",
  },
  planList: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    gap: "2px",
    marginTop: "4px",
    /* 스크롤 영역이 항목 밖 선택 막대를 자르지 않도록 좌우 여유를 둔다. */
    paddingInline: "6px",
    marginInline: "-6px",
    minHeight: 0,
    overflowY: "auto",
    flexShrink: 1,
  },
  sectionLabel: {
    height: "24px",
    marginTop: 0,
    marginBottom: "2px",
    padding: "0 6px",
    fontSize: "11px",
    lineHeight: "24px",
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flexShrink: 0,
    transitionProperty: "height, margin-top, margin-bottom",
    transitionDuration: NAV_MOTION,
    transitionTimingFunction: NAV_EASE,
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  /* 레일에서는 글자 칸을 걷어 아이콘 위 빈 줄을 남기지 않는다. */
  sectionLabelShut: {
    height: "0px",
    marginTop: "0px",
    marginBottom: "-2px",
  },
  trafficPad: {
    height: "0px",
    flexShrink: 0,
    transitionProperty: "height, background-color",
    transitionDuration: NAV_MOTION,
    transitionTimingFunction: NAV_EASE,
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  accountBar: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    padding: `4px ${tokens.spacingHorizontalS} 8px`,
  },
  /* 레일에서는 아바타 버튼만 48px 안에 둔다. 이름은 화면 밖에 둔다. */
  accountBarInset: {
    paddingLeft: "4px",
    paddingRight: "0px",
    justifyContent: "flex-start",
  },
  userBtn: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: 0,
    flex: 1,
    width: "100%",
    height: "44px",
    padding: "0 8px",
    border: "none",
    borderRadius: tokens.borderRadiusLarge,
    background: "transparent",
    color: tokens.colorNeutralForeground1,
    cursor: "pointer",
    textAlign: "left",
    transitionProperty: "background-color",
    transitionDuration: tokens.durationNormal,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  userBtnOn: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  userBtnRail: {
    position: "relative",
    flex: "0 0 auto",
    width: "40px",
    minWidth: "40px",
    maxWidth: "40px",
    height: "40px",
    paddingLeft: "0px",
    paddingRight: "0px",
    justifyContent: "center",
  },
  userTextRail: {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: "0px",
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: "0",
  },
  userText: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 0,
    flex: "1 1 auto",
    textAlign: "left",
  },
  userName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: tokens.fontWeightRegular,
    color: "var(--nav-ink)",
  },
  userEmail: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    fontSize: "11px",
    lineHeight: "14px",
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
  },
  popoverCard: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    width: "220px",
  },
  popoverLabel: {
    fontSize: "11px",
    lineHeight: "14px",
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
  },
  popoverMenu: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  popoverItem: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    width: "100%",
    minHeight: "28px",
    padding: `0 ${tokens.spacingHorizontalS}`,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    background: "transparent",
    color: tokens.colorNeutralForeground1,
    fontSize: "13px",
    lineHeight: "18px",
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    textAlign: "left",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  popoverItemOn: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  popoverRule: {
    height: "1px",
    backgroundColor: tokens.colorNeutralStroke2,
    margin: "4px 0",
  },
  menuIcon: {
    display: "inline-flex",
    flexShrink: 0,
    width: "20px",
    height: "20px",
  },
  itemLabel: {
    minWidth: 0,
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  itemChevron: {
    flexShrink: 0,
    width: "12px",
    height: "12px",
    color: tokens.colorNeutralForeground3,
  },
  flyout: {
    position: "relative",
  },
  flyoutPanel: {
    position: "absolute",
    bottom: 0,
    left: "100%",
    zIndex: 2,
    paddingLeft: "4px",
  },
  flyoutCard: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    width: "248px",
    maxHeight: "min(440px, calc(100vh - 24px))",
    overflow: "auto",
    padding: "6px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
  },
});

function LabCountItem({ count, onClose }: { count: TabLabCount; onClose: () => void }) {
  const styles = useStyles();
  const current = useTabLabCount();
  const on = current === count;
  const label = `탭 ${count}개`;
  return (
    <button
      type="button"
      className={mergeClasses(styles.popoverItem, on && styles.popoverItemOn)}
      aria-pressed={on}
      onClick={() => {
        setTabLabCount(on ? null : count);
        onClose();
      }}
    >
      <Tab20Regular className={styles.menuIcon} />
      <span className={styles.itemLabel} title={label}>{label}</span>
    </button>
  );
}

const ROLE_ICON = {
  owner: Key20Regular,
  admin: Shield20Regular,
  editor: Edit20Regular,
  contributor: Person20Regular,
  viewer: Eye20Regular,
} as const;

function MenuRow({
  icon,
  label,
  onClick,
  pressed,
}: {
  icon: ReactElement;
  label: string;
  onClick?: () => void;
  pressed?: boolean;
}) {
  const styles = useStyles();
  return (
    <button
      type="button"
      className={mergeClasses(styles.popoverItem, pressed && styles.popoverItemOn)}
      aria-pressed={pressed}
      onClick={onClick}
    >
      <span className={styles.menuIcon}>{icon}</span>
      <span className={styles.itemLabel} title={label}>{label}</span>
    </button>
  );
}

/** 계정 메뉴 옆 하위 목록. 부모 팝오버 안에 두어 바깥 클릭으로 같이 닫히지 않게 한다. */
function MenuFlyout({ icon, label, children }: { icon: ReactElement; label: string; children: ReactNode }) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.flyout} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        className={mergeClasses(styles.popoverItem, open && styles.popoverItemOn)}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight") {
            e.preventDefault();
            setOpen(true);
          }
          if (e.key === "ArrowLeft" && open) {
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
          }
        }}
      >
        <span className={styles.menuIcon}>{icon}</span>
        <span className={styles.itemLabel}>{label}</span>
        <ChevronRight12Regular className={styles.itemChevron} />
      </button>
      {open && (
        <div className={styles.flyoutPanel}>
          <div className={styles.flyoutCard} role="menu" aria-label={label}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

function AccountMenu({
  onClose,
  onDemo,
  onAccount,
  signedIn,
  onLogin,
  onLogout,
}: {
  onClose: () => void;
  onDemo: () => void;
  signedIn: boolean;
  onLogin: () => void;
  onLogout: () => void;
  onAccount: () => void;
}) {
  const styles = useStyles();
  const navigate = useNavigate();
  const theme = useCatalogStore((s) => s.theme);
  const setTheme = useCatalogStore((s) => s.setTheme);
  const setUI = useProjectStore((s) => s.setUI);
  const previewPlanRole = useProjectStore((s) => s.previewPlanRole);
  const setPreviewPlanRole = useProjectStore((s) => s.setPreviewPlanRole);
  const previewWorkspaceAdmin = useProjectStore((s) => s.previewWorkspaceAdmin);
  const setPreviewWorkspaceAdmin = useProjectStore((s) => s.setPreviewWorkspaceAdmin);
  const previewSelfResourceId = useProjectStore((s) => s.previewSelfResourceId);
  const setPreviewSelfResourceId = useProjectStore((s) => s.setPreviewSelfResourceId);
  const previewGuest = useProjectStore((s) => s.previewGuest);
  const setPreviewGuest = useProjectStore((s) => s.setPreviewGuest);
  const previewWorkspaceMember = useProjectStore((s) => s.previewWorkspaceMember);
  const setPreviewWorkspaceMember = useProjectStore((s) => s.setPreviewWorkspaceMember);
  const planTasks = useProjectStore((s) => s.project.tasks);
  const planOpen = useProjectStore((s) => !!s.activeId);
  const resources = useCatalogStore((s) => s.resources);
  const assignedPeople = useMemo(() => {
    const names: Record<string, string> = {};
    for (const [id, resource] of Object.entries(resources)) names[id] = resource.displayName;
    return peopleOnPlan(planTasks, names);
  }, [planTasks, resources]);
  const appSettingsOn = useSettingsHub((s) => (s.scope === "app" || s.scope === "workspace") && s.section !== "shortcuts");
  const dark = useResolvedDark(theme);
  const labCount = useTabLabCount();

  const toggleTheme = () => {
    const next = dark ? "light" : "dark";
    setTheme(next);
    setUI({ theme: next });
  };

  const go = (fn: () => void) => {
    onClose();
    fn();
  };

  return (
    <div className={styles.popoverCard}>
      <div className={styles.popoverMenu}>
        <MenuRow icon={<PersonSettings20Regular />} label="계정 설정" onClick={() => go(onAccount)} />
        <MenuRow
          icon={<Settings20Regular />}
          label={`앱 설정 (${modKey},)`}
          pressed={appSettingsOn}
          onClick={() => go(() => useSettingsHub.getState().open("app"))}
        />
        <MenuRow
          icon={signedIn ? <ArrowExit20Regular /> : <ArrowEnter20Regular />}
          label={signedIn ? "로그아웃" : "로그인"}
          onClick={() => go(signedIn ? onLogout : onLogin)}
        />
        <MenuRow
          icon={dark ? <WeatherSunny20Regular /> : <WeatherMoon20Regular />}
          label={dark ? "라이트 모드" : "다크 모드"}
          onClick={toggleTheme}
        />
        <MenuRow
          icon={<Keyboard20Regular />}
          label={`단축키 (${modKey}/)`}
          onClick={() => go(() => useSettingsHub.getState().open("app", "shortcuts"))}
        />
        <MenuFlyout icon={<LocalLanguage20Regular />} label="언어">
          <MenuRow icon={<LocalLanguage20Regular />} label="한글" />
          <MenuRow icon={<Globe20Regular />} label="영어" />
        </MenuFlyout>
        <div className={styles.popoverRule} />
        <MenuFlyout icon={<Bug20Regular />} label="디버그">
          <span className={styles.popoverLabel}>이 역할로 보기</span>
          {MEMBER_PLAN_ROLES.map((role) => {
            const on = !previewWorkspaceAdmin && (previewPlanRole ?? "owner") === role;
            const Icon = ROLE_ICON[role];
            return (
              <MenuRow
                key={role}
                icon={<Icon />}
                label={planRoleLabel(role)}
                pressed={on}
                onClick={() => {
                  const from = previewPlanRole ?? "owner";
                  noteRoleDemotion(from, role, useProjectStore.getState().saveState === "dirty");
                  setPreviewPlanRole(role === "owner" ? null : role);
                }}
              />
            );
          })}
          <MenuRow
            icon={<People20Regular />}
            label="워크스페이스 관리자로 보기"
            pressed={previewWorkspaceAdmin}
            onClick={() => setPreviewWorkspaceAdmin(!previewWorkspaceAdmin)}
          />
          <MenuRow
            icon={<PersonGuest20Regular />}
            label="게스트로 보기"
            pressed={previewGuest}
            onClick={() => setPreviewGuest(!previewGuest)}
          />
          <MenuRow
            icon={<PeopleTeam20Regular />}
            label="참여자로 보기"
            pressed={previewWorkspaceMember}
            onClick={() => setPreviewWorkspaceMember(!previewWorkspaceMember)}
          />
          {previewPlanRole === "contributor" && planOpen && (
            <>
              <span className={styles.popoverLabel}>내 배정</span>
              {assignedPeople.map((person) => {
                const on = previewSelfResourceId === person.id;
                return (
                  <MenuRow
                    key={person.id}
                    icon={<Person20Regular />}
                    label={person.name}
                    pressed={on}
                    onClick={() => setPreviewSelfResourceId(on ? null : person.id)}
                  />
                );
              })}
            </>
          )}
          <div className={styles.popoverRule} />
          <span className={styles.popoverLabel}>실험실</span>
          {([5, 10, 20] as const).map((count) => (
            <LabCountItem key={count} count={count} onClose={onClose} />
          ))}
          {labCount != null && (
            <MenuRow
              icon={<ArrowCounterclockwise20Regular />}
              label="원래 탭"
              onClick={() => {
                setTabLabCount(null);
                onClose();
              }}
            />
          )}
          <MenuRow icon={<Beaker20Regular />} label="초기화" onClick={() => go(onDemo)} />
          <MenuRow icon={<Color20Regular />} label="디자인 시스템" onClick={() => go(() => navigate("/design-system"))} />
        </MenuFlyout>
      </div>
    </div>
  );
}

function RailUtility({ open, children }: { open: boolean; children: ReactNode }) {
  const styles = useStyles();
  return (
    <div
      className={mergeClasses(styles.railUtility, open && styles.railUtilityOpen)}
      inert={!open || undefined}
      aria-hidden={!open || undefined}
    >
      <div className={styles.railUtilityInner}>
        <div className={styles.railUtilityPad}>{children}</div>
      </div>
    </div>
  );
}

function AccountBar({ collapsed }: { collapsed: boolean }) {
  const styles = useStyles();
  const navigate = useNavigate();
  const displayName = useCatalogStore((s) => s.profile.displayName);
  const profileEmail = useCatalogStore((s) => s.profile.email);
  const selfEmail = useCatalogStore((s) => s.resources.res_me?.email);
  const avatar = useCatalogStore((s) => s.profile.avatar);
  const loadDemoCatalog = useProjectStore((s) => s.loadDemoCatalog);

  const [profileOpen, setProfileOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const session = useSession();
  const accountEmail = (session?.email || profileEmail || selfEmail || "").trim();
  const accountName = (displayName || "").trim() || "사용자";
  const fadeClass = mergeClasses(styles.fade, collapsed && styles.fadeHidden);

  return (
    <>
      <div className={mergeClasses(styles.accountBar, collapsed && styles.accountBarInset)}>
        <Popover open={profileOpen} onOpenChange={setProfileOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={mergeClasses(styles.userBtn, collapsed && styles.userBtnRail, profileOpen && styles.userBtnOn)}
              aria-label={`${accountName}${accountEmail ? ` (${accountEmail})` : ""} 계정 메뉴`}
              aria-haspopup="dialog"
              aria-expanded={profileOpen}
              title={collapsed ? `${accountName}${accountEmail ? ` (${accountEmail})` : ""}` : undefined}
            >
              <PersonAvatar name={accountName} size={32} image={avatar} tip={false} />
              <div className={mergeClasses(styles.userText, fadeClass, collapsed && styles.userTextRail)} aria-hidden={collapsed || undefined}>
                <span className={styles.userName} title={accountName}>
                  {accountName}
                </span>
                {accountEmail && (
                  <span className={styles.userEmail} title={accountEmail}>
                    {accountEmail}
                  </span>
                )}
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side={collapsed ? "right" : "top"}
            className="w-auto p-2"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <AccountMenu
              onClose={() => setProfileOpen(false)}
              onDemo={() => setDemoOpen(true)}
              onAccount={() => useSettingsHub.getState().open("app", "account")}
              signedIn={!!session}
              onLogin={() => navigate("/login")}
              onLogout={() => setLogoutOpen(true)}
            />
          </PopoverContent>
        </Popover>
      </div>

      <AccountSettingsDialog open={accountOpen} onClose={() => setAccountOpen(false)} />
      <AppDialog
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        title="목업 초기화"
        description="이 워크스페이스의 플랜과 인력을 최근 목업으로 다시 채웁니다."
        width="sm"
        footer={
          <DialogActionsBar
            cancelLabel="취소"
            confirmLabel="초기화"
            onCancel={() => setDemoOpen(false)}
            onConfirm={() => {
              loadDemoCatalog();
              setDemoOpen(false);
              navigate("/");
            }}
          />
        }
      >
        <Caption1 style={{ color: tokens.colorNeutralForeground2 }}>
          이 워크스페이스의 플랜과 팀·인력을 최근 목업으로 다시 채웁니다. 다른 워크스페이스는 그대로 둡니다. 지금 이 워크스페이스에서 편집 중인 내용은 사라집니다.
        </Caption1>
      </AppDialog>
      <ConfirmDialog
        open={logoutOpen}
        title="로그아웃"
        message="이 브라우저의 로그인을 끝냅니다. 로컬 플랜은 그대로 둡니다."
        confirmLabel="로그아웃"
        onCancel={() => setLogoutOpen(false)}
        onConfirm={() => {
          signOut();
          setLogoutOpen(false);
        }}
      />
    </>
  );
}

/** 사이드바 크롬의 마크. 3단 태스크 스택 막대와 대각선 축이 교차하며 Z·X 형태를 이룬다. 파비콘(`src/favicon.svg`)과 같은 도형이다. */
function NavMark() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M 4.25 10.25 H 8.40 Q 9.21 10.25 8.67 10.85 L 6.33 13.48 Q 6.09 13.75 5.73 13.75 H 4.25 A 1.75 1.75 0 0 1 4.25 10.25 Z"
        fill="currentColor"
        fillOpacity="0.42"
      />
      <path
        d="M 18.27 10.25 H 19.75 A 1.75 1.75 0 0 1 19.75 13.75 H 15.60 Q 14.79 13.75 15.33 13.15 L 17.67 10.52 Q 17.91 10.25 18.27 10.25 Z"
        fill="currentColor"
        fillOpacity="0.42"
      />
      <path
        d="M 4.55 3.75 H 14.20 Q 15.01 3.75 14.47 4.35 L 12.13 6.98 Q 11.89 7.25 11.53 7.25 H 4.55 A 1.75 1.75 0 0 1 4.55 3.75 Z"
        fill="currentColor"
      />
      <path
        d="M 12.47 16.75 H 19.45 A 1.75 1.75 0 0 1 19.45 20.25 H 9.80 Q 8.99 20.25 9.53 19.65 L 11.87 17.02 Q 12.11 16.75 12.47 16.75 Z"
        fill="currentColor"
      />
      <path
        d="M 17.8 5.5 L 6.2 18.5"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function AppNav() {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useSurfaceLocation();
  const collapsed = useCatalogStore((s) => s.navCollapsed);

  const projects = useCatalogStore((s) => s.projects);
  const byProject = useCatalogStore((s) => s.byProject);
  const previewGuest = useProjectStore((s) => s.previewGuest);
  const guestPlanId = useProjectStore((s) => s.previewGuestPlanId);
  const workspaceRole = useWorkspaceRole();
  const allowCreate = canWorkspace(workspaceRole, "plan.create");
  const visibilityTick = usePlanVisibilityTick();
  const plans = useMemo(
    () => {
      const rows = Object.values(projects)
        .map((p) => ({ id: p.id, name: p.name, updatedAt: p.updatedAt, lastTab: byProject[p.id]?.lastTab ?? "schedule" }))
        .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0));
      if (previewGuest) return guestPlanId ? rows.filter((row) => row.id === guestPlanId) : [];
      return rows.filter((row) => showInPlanList(planVisibilityOf(row.id), workspaceRole).shown);
    },
    [projects, byProject, previewGuest, guestPlanId, workspaceRole, visibilityTick],
  );
  useEffect(() => {
    if (!previewGuest) return;
    const path = location.pathname;
    const blocked = path.startsWith("/portfolio") || path.startsWith("/resources") || path.startsWith("/members") || path.startsWith("/teams") || path === "/projects/new" || path === "/files" || path === "/docs" || path === "/tasks";
    const otherPlan = pathnameOpensPlan(path) && !!guestPlanId && !pathnameOpensPlan(path, guestPlanId);
    if (blocked || otherPlan) navigate(guestPlanId ? planHref(guestPlanId, "schedule") : "/", { replace: true });
  }, [previewGuest, guestPlanId, location.pathname, navigate]);
  useEffect(() => {
    if (allowCreate) return;
    if (location.pathname === "/projects/new") navigate("/", { replace: true });
  }, [allowCreate, location.pathname, navigate]);
  const myOpenCount = useCatalogStore((s) => {
    const me = s.profile.displayName;
    let n = 0;
    for (const p of Object.values(s.projects)) for (const t of p.executionTasks) if (t.status !== "done" && t.assignees.includes(me)) n++;
    return n;
  });

  const fadeClass = mergeClasses(styles.fade, collapsed && styles.fadeHidden);
  const activeItem = styles.itemOn;
  const onHome = location.pathname === "/home";
  const onPlans = location.pathname === "/" || location.pathname.startsWith("/plans");
  const onPortfolio = location.pathname.startsWith("/portfolio");
  const onTasks = location.pathname === "/tasks";
  const onDocs = location.pathname === "/docs";
  const onFiles = location.pathname === "/files";
  const onWorkspaceActivity = location.pathname === "/activity";
  const onResources = location.pathname.startsWith("/resources");
  const onMembers = location.pathname.startsWith("/members") || location.pathname.startsWith("/teams");

  const routeProjectId = projectIdFromPath(location.pathname);
  const projectExists = useCatalogStore((s) => (routeProjectId ? !!s.projects[routeProjectId] : false));
  const inProject = !!routeProjectId && projectExists;
  // 플랜을 떠난 뒤에도 슬라이드가 끝날 때까지 이전 플랜 메뉴를 그대로 보여 준다.
  const [paneProjectId, setPaneProjectId] = useState<string | null>(inProject ? routeProjectId : null);
  if (inProject && routeProjectId !== paneProjectId) setPaneProjectId(routeProjectId);

  const shareOpen = useShareOpen();
  const [settingsTab, setSettingsTab] = useState<PlanSettingsTab | undefined>();
  const planSettingsOpen = useProjectStore((s) => s.dialog === "plan-settings");
  const livePlanId = useProjectStore((s) => s.activeId);
  const livePlanName = useProjectStore((s) => s.project.name);
  const workspaceName = useCatalogStore((s) => s.workspaces[s.activeWorkspaceId]?.name ?? "워크스페이스");
  const routePlan = routeProjectId && projectExists ? projects[routeProjectId] : undefined;
  useEffect(() => {
    if (planSettingsOpen && !routePlan) useProjectStore.getState().openDialog(null);
    if (!planSettingsOpen) setSettingsTab(undefined);
  }, [planSettingsOpen, routePlan]);

  const openPlan = (id: string, tab: string) => {
    const store = useProjectStore.getState();
    if (store.activeId !== id) store.openProject(id);
    navigate(planHref(id, tab));
  };

  const globalPaneRef = useRef<HTMLDivElement>(null);
  const projectPaneRef = useRef<HTMLDivElement>(null);
  const focusOnSwitch = useRef(false);
  const markNavFocus = () => {
    focusOnSwitch.current = true;
  };
  useEffect(() => {
    if (!focusOnSwitch.current) return;
    focusOnSwitch.current = false;
    const pane = inProject ? projectPaneRef.current : globalPaneRef.current;
    const target =
      (!inProject && paneProjectId ? pane?.querySelector<HTMLElement>(`[data-plan-id="${CSS.escape(paneProjectId)}"]`) : null) ??
      pane?.querySelector<HTMLElement>("[data-nav-entry]");
    target?.focus({ preventScroll: true });
  }, [inProject, paneProjectId]);

  return (
    <nav
      className={styles.slot}
      data-app-nav
      data-collapsed={collapsed ? "" : undefined}
      id="app-nav"
      aria-label="앱 탐색"
    >
      <div className={styles.inner}>
        <div
          className={styles.chrome}
          data-nav-chrome
          data-nav-header
          data-tauri-drag-region
        >
          <NavTip label="사이드바 열기" enabled={collapsed} hug>
            <button
              type="button"
              className={styles.brand}
              data-chrome-mark
              aria-label={collapsed ? "사이드바 열기" : inProject ? "워크스페이스" : "홈"}
              onClick={() => {
                if (collapsed) {
                  useCatalogStore.getState().setNavCollapsed(false);
                  return;
                }
                if (inProject) {
                  markNavFocus();
                  navigate({ pathname: "/", hash: "" });
                  return;
                }
                navigate("/home");
              }}
            >
              <NavMark />
            </button>
          </NavTip>
          <div className={styles.chromeEnd} data-chrome-end="" inert={collapsed || undefined} aria-hidden={collapsed || undefined}>
            <ChromeSearch />
            <ChromeToggle floating={false} />
          </div>
        </div>
        <div className={styles.trafficPad} data-traffic-pad data-tauri-drag-region />
        <RailUtility open={collapsed}>
          <ChromeSearch />
        </RailUtility>
        <div className={styles.viewport}>
          <div className={mergeClasses(styles.track, inProject && styles.trackProject)}>
            {/* 워크스페이스 메뉴 */}
            <div
              ref={globalPaneRef}
              className={mergeClasses(styles.pane, inProject && styles.paneAway)}
              inert={inProject}
              aria-hidden={inProject || undefined}
            >
              <div
                className={mergeClasses(styles.header, collapsed && styles.headerShut)}
                data-nav-header
                data-tauri-drag-region
                inert={collapsed || undefined}
                aria-hidden={collapsed || undefined}
              />
              {/* 워크스페이스 선택기는 크롬 줄 아래, 사이드바 본문 맨 위에 둔다. */}
              <div className={mergeClasses(styles.switcherRow, styles.switcherRowWorkspace, collapsed && styles.switcherRowShut)} inert={collapsed || undefined}>
                <div className={mergeClasses(styles.switcherSlot, fadeClass)}>
                  <WorkspaceSwitcher labelsVisible />
                </div>
              </div>

              <div className={styles.body}>
                <div
                  className={mergeClasses(styles.sectionLabel, fadeClass, collapsed && styles.sectionLabelShut)}
                  aria-hidden={collapsed || undefined}
                >
                  워크스페이스
                </div>
                <RailButton
                  collapsed={collapsed}
                  className={mergeClasses(styles.item, onHome && activeItem)}
                  appearance="subtle"
                  icon={onHome ? <Home20Filled /> : <Home20Regular />}
                  label="홈"
                  onClick={() => navigate("/home")}
                >
                  <span className={fadeClass}>홈</span>
                </RailButton>

                <RailButton
                  collapsed={collapsed}
                  className={mergeClasses(styles.item, onPlans && activeItem)}
                  appearance="subtle"
                  icon={onPlans ? <Apps20Filled /> : <Apps20Regular />}
                  label="플랜"
                  data-nav-entry
                  onClick={() => navigate({ pathname: "/", hash: "" })}
                >
                  <span className={fadeClass}>플랜</span>
                </RailButton>

                {!previewGuest && (
                <RailButton
                  collapsed={collapsed}
                  className={mergeClasses(styles.item, onTasks && activeItem)}
                  appearance="subtle"
                  icon={onTasks ? <TaskListSquareLtr20Filled /> : <TaskListSquareLtr20Regular />}
                  label={myOpenCount > 0 ? `내 작업 · ${myOpenCount}` : "내 작업"}
                  onClick={() => navigate("/tasks")}
                >
                  <span className={mergeClasses(fadeClass, styles.labelRow)}>
                    <span className={styles.labelText}>내 작업</span>
                    {myOpenCount > 0 && <span className={styles.count}>{myOpenCount}</span>}
                  </span>
                </RailButton>
                )}

                {!previewGuest && (
                <RailButton
                  collapsed={collapsed}
                  className={mergeClasses(styles.item, onDocs && activeItem)}
                  appearance="subtle"
                  icon={onDocs ? <Document20Filled /> : <Document20Regular />}
                  label="문서"
                  onClick={() => navigate("/docs")}
                >
                  <span className={fadeClass}>문서</span>
                </RailButton>
                )}

                {!previewGuest && (
                <RailButton
                  collapsed={collapsed}
                  className={mergeClasses(styles.item, onFiles && activeItem)}
                  appearance="subtle"
                  icon={onFiles ? <Folder20Filled /> : <Folder20Regular />}
                  label="파일"
                  onClick={() => navigate("/files")}
                >
                  <span className={fadeClass}>파일</span>
                </RailButton>
                )}

                {!previewGuest && (
                <RailButton
                  collapsed={collapsed}
                  className={mergeClasses(styles.item, onWorkspaceActivity && activeItem)}
                  appearance="subtle"
                  icon={onWorkspaceActivity ? <History20Filled /> : <History20Regular />}
                  label="활동"
                  onClick={() => navigate("/activity")}
                >
                  <span className={fadeClass}>활동</span>
                </RailButton>
                )}

                {!previewGuest && (
                <RailButton
                  collapsed={collapsed}
                  className={mergeClasses(styles.item, onPortfolio && activeItem)}
                  appearance="subtle"
                  icon={onPortfolio ? <ChartMultiple20Filled /> : <ChartMultiple20Regular />}
                  label="포트폴리오"
                  onClick={() => navigate("/portfolio")}
                >
                  <span className={fadeClass}>포트폴리오</span>
                </RailButton>
                )}

                {!previewGuest && (
                <RailButton
                  collapsed={collapsed}
                  className={mergeClasses(styles.item, onResources && activeItem)}
                  appearance="subtle"
                  icon={onResources ? <PeopleTeam20Filled /> : <PeopleTeam20Regular />}
                  label="리소스"
                  onClick={() => navigate("/resources")}
                >
                  <span className={fadeClass}>리소스</span>
                </RailButton>
                )}

                {!previewGuest && (
                <RailButton
                  collapsed={collapsed}
                  className={mergeClasses(styles.item, onMembers && activeItem)}
                  appearance="subtle"
                  icon={onMembers ? <People20Filled /> : <People20Regular />}
                  label="멤버"
                  onClick={() => navigate("/members")}
                >
                  <span className={fadeClass}>멤버</span>
                </RailButton>
                )}

                <div
                  className={mergeClasses(styles.planListSlot, collapsed && styles.planListSlotShut)}
                  inert={collapsed || undefined}
                  aria-hidden={collapsed || undefined}
                >
                  <div className={styles.planListSlotInner}>
                    <div className={mergeClasses(styles.planList, fadeClass)}>
                      <div className={styles.groupRule} aria-hidden />
                      <div className={styles.sectionLabel}>플랜</div>
                      {plans.length === 0 && (
                        <Caption1 style={{ paddingInline: tokens.spacingHorizontalS, color: tokens.colorNeutralForeground3 }}>아직 없습니다</Caption1>
                      )}
                      {plans.map((p) => {
                        const active = pathnameOpensPlan(location.pathname, p.id);
                        return (
                          <FuiButton
                            key={p.id}
                            className={mergeClasses(styles.item, styles.itemCompact, active && activeItem)}
                            appearance="subtle"
                            icon={active ? <Board20Filled /> : <Board20Regular />}
                            title={p.name}
                            data-plan-id={p.id}
                            onClick={() => {
                              markNavFocus();
                              openPlan(p.id, p.lastTab);
                            }}
                          >
                            <span className={styles.labelText}>{p.name}</span>
                            {planVisibilityOf(p.id) === "private" ? <LockClosed16Regular aria-label="비공개" /> : null}
                          </FuiButton>
                        );
                      })}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* 플랜 메뉴 */}
            <div
              ref={projectPaneRef}
              className={mergeClasses(styles.pane, !inProject && styles.paneAway)}
              inert={!inProject}
              aria-hidden={!inProject || undefined}
            >
              {paneProjectId && (
                <ProjectPane
                  projectId={paneProjectId}
                  collapsed={collapsed}
                  fadeClass={fadeClass}
                  activeItem={activeItem}
                  onBack={() => {
                    markNavFocus();
                    navigate({ pathname: "/", hash: "" });
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <RailUtility open={collapsed}>
          <ChromeToggle floating={false} />
        </RailUtility>
        <AccountBar collapsed={collapsed} />
      </div>
      {routePlan && (
        <PlanShareDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          planName={routePlan.name}
          workspaceName={workspaceName}
          onOpenSettings={() => {
            setShareOpen(false);
            setSettingsTab("security");
            useSettingsHub.getState().open("plan", "security");
          }}
        />
      )}
      {routePlan && (
        <PlanSettingsDialog
          open={planSettingsOpen}
          onClose={() => useProjectStore.getState().openDialog(null)}
          planName={routePlan.id === livePlanId ? livePlanName : routePlan.name}
          initialTab={settingsTab}
        />
      )}
    </nav>
  );
}

type PlanSummary = { id: string; name: string; lastTab: ProjectTab };
type NavCount = { n: number };

function ProjectPane({
  projectId,
  collapsed,
  fadeClass,
  activeItem,
  onBack,
}: {
  projectId: string;
  collapsed: boolean;
  fadeClass: string;
  activeItem: string;
  onBack: () => void;
}) {
  const styles = useStyles();
  const location = useSurfaceLocation();
  const navigate = useNavigate();
  const openTasks = useProjectStore((s) =>
    s.activeId === projectId ? s.project.executionTasks.filter((t) => t.status !== "done").length : 0,
  );
  const openIssues = useProjectStore((s) =>
    s.activeId === projectId ? s.project.records.filter((r) => r.kind === "issue" && recordPhase(r.status) === "open").length : 0,
  );
  const pendingIntakes = useProjectStore((s) =>
    s.activeId === projectId ? (s.project.intakes ?? []).filter((i) => i.status === "pending").length : 0,
  );
  // 참여 인원: 한 사람이 역할을 여러 개 맡아도 한 명으로 센다.
  const teamSize = useProjectStore((s) =>
    s.activeId === projectId ? new Set((s.project.resourceAssignments ?? []).map((a) => a.resourceId)).size : 0,
  );

  const canReadCost = useCan("plan.cost.read");
  const adminView = useWorkspaceAdminView();
  const navGroups = adminView === "meta" ? [] : PROJECT_NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.value !== "budget" || canReadCost),
  })).filter((group) => group.items.length > 0);
  const onActivity = projectIdFromPath(location.pathname) === projectId && location.pathname.split("/").filter(Boolean)[2] === "activity";
  const routeTab = projectIdFromPath(location.pathname) === projectId ? tabFromPath(location.pathname) : null;
  const [currentTab, setCurrentTab] = useState<ProjectTab | null>(routeTab);
  if (routeTab && routeTab !== currentTab) setCurrentTab(routeTab);
  const counts: Partial<Record<ProjectTab, NavCount>> = {
    tasks: { n: openTasks },
    records: { n: openIssues },
    intake: { n: pendingIntakes },
    team: { n: teamSize },
  };
  const liveName = useProjectStore((s) => (s.activeId === projectId ? s.project.name : ""));
  const catalogName = useCatalogStore((s) => s.projects[projectId]?.name ?? "");
  const planName = liveName || catalogName || "플랜";
  const planSettingsOn = useSettingsHub((s) => s.scope === "plan");
  const openPlanSettings = () => {
    const hub = useSettingsHub.getState();
    if (hub.scope === "plan") hub.close();
    else hub.open("plan");
  };

  return (
    <>
      <div
        className={mergeClasses(styles.header, collapsed && styles.headerShut)}
        data-nav-header
        data-tauri-drag-region
        inert={collapsed || undefined}
        aria-hidden={collapsed || undefined}
      />

      <div className={mergeClasses(styles.body, styles.bodyScroll)}>
        <div
          className={mergeClasses(styles.planIdentitySlot, collapsed && styles.planIdentitySlotShut)}
          inert={collapsed || undefined}
          aria-hidden={collapsed || undefined}
        >
          <div className={styles.planIdentity}>
            <div className={styles.planIdentityRow}>
            <button type="button" className={styles.planBack} title={planName} aria-label={`${planName}, 플랜 목록`} onClick={onBack}>
              <span className={styles.planBackMark} aria-hidden>
                <ChevronLeft16Regular />
              </span>
              <span className={styles.planIdentityName}>{planName}</span>
            </button>
            <NavTip label="플랜 설정" side="bottom" hug>
              <FuiButton
                appearance="subtle"
                className={mergeClasses(styles.planIdentityIcon, styles.planSettingsIcon, planSettingsOn && activeItem)}
                icon={<Settings16Regular />}
                aria-label="플랜 설정"
                aria-pressed={planSettingsOn}
                onClick={openPlanSettings}
              />
            </NavTip>
            </div>
          </div>
        </div>
        <div className={mergeClasses(styles.railSlot, collapsed && styles.railSlotOpen)} inert={!collapsed || undefined}>
          <div className={styles.railSlotInner}>
            <RailButton
              collapsed={collapsed}
              className={styles.item}
              appearance="subtle"
              icon={<ArrowLeft20Regular />}
              label="플랜 목록으로 돌아가기"
              data-nav-entry
              onClick={onBack}
            >
              <span className={fadeClass}>플랜 목록</span>
            </RailButton>
            <RailButton
              collapsed={collapsed}
              className={mergeClasses(styles.item, planSettingsOn && activeItem)}
              appearance="subtle"
              icon={<Settings20Regular />}
              label="플랜 설정"
              aria-pressed={planSettingsOn}
              onClick={openPlanSettings}
            >
              <span className={fadeClass}>플랜 설정</span>
            </RailButton>
          </div>
        </div>

      {navGroups.map((group, i) => (
        <ProjectNavSection
          key={group.id}
          group={group}
          first={i === 0}
          rail={collapsed}
          currentTab={currentTab}
          counts={counts}
          collapsed={collapsed}
          fadeClass={fadeClass}
          activeItem={activeItem}
          onSelect={(tab) => navigate(planHref(projectId, tab))}
        />
      ))}

    </div>
    </>
  );
}

function ProjectNavSection({
  group,
  first,
  rail,
  currentTab,
  counts,
  collapsed,
  fadeClass,
  activeItem,
  onSelect,
}: {
  group: ProjectNavGroup;
  first: boolean;
  rail: boolean;
  currentTab: ProjectTab | null;
  counts: Partial<Record<ProjectTab, NavCount>>;
  collapsed: boolean;
  fadeClass: string;
  activeItem: string;
  onSelect: (tab: ProjectTab) => void;
}) {
  const styles = useStyles();
  const bodyId = `nav-group-${group.id}`;

  const items = group.items.map((item) => {
    const on = currentTab === item.value;
    const Icon = on ? item.iconOn : item.icon;
    const count = counts[item.value];
    return (
      <RailButton
        key={item.value}
        collapsed={collapsed}
        className={mergeClasses(styles.item, on && activeItem)}
        appearance="subtle"
        icon={<Icon />}
        label={count && count.n > 0 ? `${item.label} · ${count.n}` : item.label}
        aria-current={on ? "page" : undefined}
        onClick={() => onSelect(item.value)}
      >
        <span className={mergeClasses(fadeClass, styles.labelRow)}>
          <span className={styles.labelText}>{item.label}</span>
          {count && count.n > 0 && <span className={styles.count}>{count.n}</span>}
        </span>
      </RailButton>
    );
  });

  if (!group.label) {
    return (
      <div className={styles.group}>
        {!first && !rail && <div className={styles.groupRule} aria-hidden />}
        {!first && <div className={mergeClasses(styles.railDivider, rail && styles.railDividerOn)} aria-hidden />}
        <div className={styles.groupInner}>{items}</div>
      </div>
    );
  }

  return (
    <div className={styles.group} role={rail ? undefined : "group"} aria-labelledby={rail ? undefined : `${bodyId}-label`}>
      {!first && !rail && <div className={styles.groupRule} aria-hidden />}
      <div
        className={mergeClasses(styles.groupHeader, (first || !rail) && styles.groupHeaderFirst, rail && styles.groupHeaderRail)}
        aria-hidden={rail || undefined}
        inert={rail || undefined}
      >
        <span id={`${bodyId}-label`} className={mergeClasses(styles.groupLabel, fadeClass)}>
          {group.label}
        </span>
      </div>
      {!first && <div className={mergeClasses(styles.railDivider, rail && styles.railDividerOn)} aria-hidden />}
      <div id={bodyId}>
        <div className={styles.groupInner}>{items}</div>
      </div>
    </div>
  );
}

function useResolvedDark(theme: "system" | "light" | "dark") {
  const [systemDark, setSystemDark] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches,
  );

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemDark(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  if (theme === "dark") return true;
  if (theme === "light") return false;
  return systemDark;
}

function RailButton({
  collapsed,
  label,
  children,
  className,
  ...props
}: ButtonProps & { collapsed: boolean; label: string; children: ReactNode }) {
  const styles = useStyles();
  const btn = (
    <FuiButton {...props} className={mergeClasses(className, collapsed && styles.itemRail)} aria-label={collapsed ? label : undefined}>
      {collapsed ? <span className={styles.railLabel}>{children}</span> : children}
    </FuiButton>
  );
  return (
    <NavTip label={label} enabled={collapsed}>
      {btn}
    </NavTip>
  );
}

function NavTip({
  label,
  children,
  enabled = true,
  hug = false,
  side = "right",
}: {
  label: string;
  children: ReactElement;
  enabled?: boolean;
  hug?: boolean;
  side?: "right" | "bottom" | "top";
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [box, setBox] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!enabled) setBox(null);
  }, [enabled]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const show = () => {
      const r = el.getBoundingClientRect();
      const nav = el.closest("nav")?.getBoundingClientRect();
      const right = nav ? Math.min(r.right, nav.right) : r.right;
      setBox(new DOMRect(r.x, r.y, Math.max(right - r.x, 1), r.height));
    };
    const hide = () => setBox(null);
    el.addEventListener("pointerenter", show);
    el.addEventListener("pointerleave", hide);
    el.addEventListener("focusin", show);
    el.addEventListener("focusout", hide);
    return () => {
      el.removeEventListener("pointerenter", show);
      el.removeEventListener("pointerleave", hide);
      el.removeEventListener("focusin", show);
      el.removeEventListener("focusout", hide);
    };
  }, [enabled]);

  let tipStyle: CSSProperties | undefined;
  if (box) {
    if (side === "right") tipStyle = { position: "fixed", left: box.right + 10, top: box.top + box.height / 2, transform: "translateY(-50%)" };
    else if (side === "bottom") tipStyle = { position: "fixed", left: box.left + box.width / 2, top: box.bottom + 8, transform: "translateX(-50%)" };
    else tipStyle = { position: "fixed", left: box.left + box.width / 2, top: box.top - 8, transform: "translate(-50%, -100%)" };
  }

  return (
    <>
      <span ref={ref} className={hug ? "inline-flex" : "flex min-w-0 w-full"}>
        {children}
      </span>
      {enabled &&
        box &&
        createPortal(
          <div role="tooltip" className="pointer-events-none z-[200] rounded-md bg-foreground px-2.5 py-1 text-xs text-background shadow-md" style={tipStyle}>
            {label}
          </div>,
          document.body,
        )}
    </>
  );
}
